import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import GuestVideoView from "@/components/GuestVideoView";

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";
const guestApi = axios.create({ baseURL: API_BASE });

export default function GuestJoin() {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";

  const [stage, setStage] = useState(token ? "form" : "error");
  const [name, setName] = useState("");
  const [guestId, setGuestId] = useState(null);
  const [agora, setAgora] = useState(null);
  const [errorMsg, setErrorMsg] = useState(token ? "" : "Este link no es válido.");
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const { data } = await guestApi.post(`/live-classes/${classId}/guest-join`, { token, name: trimmed });
      setGuestId(data.guest_id);
      setStage("waiting");
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setErrorMsg("Este link ya no es válido o ha expirado.");
        setStage("error");
      } else {
        setErrorMsg(err.response?.data?.detail || "Error al solicitar acceso.");
        setStage("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (stage !== "waiting" || !guestId) return;
    const poll = async () => {
      try {
        const { data } = await guestApi.get(`/live-classes/${classId}/guest-status/${guestId}`);
        if (data.status === "approved") {
          setAgora({ appId: data.agora_app_id, token: data.agora_token, uid: data.agora_uid });
          setStage("approved");
        } else if (data.status === "rejected") {
          setStage("rejected");
        }
      } catch {
        /* sigue intentando */
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [stage, guestId, classId]);

  useEffect(() => {
    if (stage !== "waiting") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  }, [stage]);

  if (stage === "approved" && agora) {
    return (
      <GuestVideoView
        appId={agora.appId}
        token={agora.token}
        channelName={classId}
        uid={agora.uid}
        guestName={name.trim()}
        guestId={guestId}
        onLeave={() => { window.location.href = "/"; }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1025] flex items-center justify-center p-4" data-testid="guest-join-page">
      <div className="w-full max-w-md bg-[#12152B] rounded-2xl border border-white/10 p-8 text-center space-y-6">
        <div>
          <h1 className="text-2xl font-black text-white">Neuromatemáticas <span className="text-[#A43790]">JASQ</span></h1>
          <p className="text-xs text-white/40 mt-1">Clase en vivo — Acceso de invitado</p>
        </div>

        {stage === "form" && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="guest-form">
            <div>
              <label className="block text-sm text-white/60 mb-1.5 text-left">Tu nombre</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                placeholder="Ej: Papá de Bruno"
                className="w-full bg-white border border-white/10 rounded-lg px-4 py-3 text-black text-sm outline-none focus:border-[#A43790] placeholder-gray-400" style={{ color: "#000", WebkitTextFillColor: "#000" }}
                data-testid="guest-name-input"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={!name.trim() || submitting}
              data-testid="guest-submit-btn"
              className="w-full py-3 rounded-lg bg-[#A43790] hover:bg-[#C44DB0] text-white font-bold text-sm transition-colors disabled:opacity-40"
            >
              {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Pedir unirme"}
            </button>
          </form>
        )}

        {stage === "waiting" && (
          <div className="space-y-4 py-4" data-testid="guest-waiting">
            <Loader2 size={32} className="animate-spin text-[#A43790] mx-auto" />
            <p className="text-sm text-white/60">Esperando que el docente te deje entrar…</p>
            <p className="text-xs text-white/30">No cierres esta ventana.</p>
          </div>
        )}

        {stage === "rejected" && (
          <div className="space-y-4 py-4" data-testid="guest-rejected">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
              <span className="text-2xl">&#10005;</span>
            </div>
            <p className="text-sm text-white/60">El docente no aprobó tu solicitud.</p>
          </div>
        )}

        {stage === "error" && (
          <div className="space-y-4 py-4" data-testid="guest-error">
            <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/20 flex items-center justify-center">
              <span className="text-2xl">&#9888;</span>
            </div>
            <p className="text-sm text-white/60">{errorMsg}</p>
          </div>
        )}
      </div>
    </div>
  );
}
